import React from 'react';
import { FiCheck, FiX } from 'react-icons/fi';
import useExtensionActionLanding from './useExtensionActionLanding';
import { Container, Card, SuccessBanner, InfoBanner } from './ExtensionActionLanding.styles';
import {
  ModalIconBadge, Label, Input, Textarea, HintText, HighlightText, ConfirmDenyBtn,
} from '../../pages/admin/extensions/adminExtensions/ExtensionModals.styles';
import { ApproveBtn, ErrMsg } from '../../pages/admin/extensions/adminExtensions/sharedButtons.styles';

// Landing page for the Approve/Deny links embedded in extension-request
// emails. See useExtensionActionLanding.js for the auth/redirect/fetch logic.
function ExtensionActionLanding() {
  const {
    authLoading, user, action, loading, error, request, result,
    hours, setHours, note, setNote, submitting, handleApprove, handleDeny,
  } = useExtensionActionLanding();

  if (authLoading || !user || loading) {
    return (
      <Container>
        <Card>
          <p>Loading…</p>
        </Card>
      </Container>
    );
  }

  const studentName = [request?.student_first_name, request?.student_last_name].filter(Boolean).join(' ');
  const courseLabel = request ? `${request.course_name}${request.course_code ? ` (${request.course_code})` : ''}` : '';

  return (
    <Container>
      <Card>
        <ModalIconBadge $tint={action === 'deny' ? 'rgba(229, 62, 62, 0.12)' : 'rgba(107, 142, 127, 0.16)'} $color={action === 'deny' ? '#c0392b' : '#466B5E'}>
          {action === 'deny' ? <FiX size={16} /> : <FiCheck size={16} />}
        </ModalIconBadge>
        <h2 style={{ marginTop: 12 }}>{action === 'deny' ? 'Deny Extension Request' : 'Approve Extension Request'}</h2>

        {error && !request && <ErrMsg>{error}</ErrMsg>}

        {result && (
          <SuccessBanner>
            {result === 'approved' ? '✅ Extension request approved.' : '✅ Extension request denied.'}
          </SuccessBanner>
        )}

        {!result && request && request.status !== 'pending' && (
          <InfoBanner>
            This request was already <strong>{request.status}</strong>
            {request.reviewed_at ? ` on ${new Date(request.reviewed_at).toLocaleString()}` : ''}.
          </InfoBanner>
        )}

        {!result && request && request.status === 'pending' && (
          <>
            <HintText>
              <HighlightText>{studentName}</HighlightText> requested an extension for{' '}
              <HighlightText>&ldquo;{request.item_title}&rdquo;</HighlightText> in {courseLabel}.
            </HintText>
            {request.justification && <HintText>Justification: {request.justification}</HintText>}

            {action === 'approve' ? (
              <>
                <Label htmlFor="approve-hours">Grant access duration (hours)</Label>
                <Input
                  id="approve-hours" type="number" min="1" max="2160" value={hours}
                  onChange={(e) => setHours(e.target.value)}
                />
                <div style={{ height: 14 }} />
                <Label htmlFor="approve-note">Note to student (optional)</Label>
                <Textarea id="approve-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
                {error && <ErrMsg>{error}</ErrMsg>}
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <ApproveBtn onClick={handleApprove} disabled={submitting}>
                    {submitting ? 'Approving…' : 'Approve Extension'}
                  </ApproveBtn>
                </div>
              </>
            ) : (
              <>
                <Label htmlFor="deny-note">Reason for student (optional)</Label>
                <Textarea id="deny-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
                {error && <ErrMsg>{error}</ErrMsg>}
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <ConfirmDenyBtn onClick={handleDeny} disabled={submitting}>
                    {submitting ? 'Denying…' : 'Deny Extension'}
                  </ConfirmDenyBtn>
                </div>
              </>
            )}
          </>
        )}
      </Card>
    </Container>
  );
}

export default ExtensionActionLanding;
